import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";
import {
  Modal,
  Button,
  MenuItem,
  Select,
  TextField,
  IconButton,
  CardMedia,
  Input,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function AddFikraStep({ open, onClose, setOpen }: ModalProps) {
  const [questionType, setQuestionType] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([""]);

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const handleSuggestionChange = (index: number, value: string) => {
    const updated = [...suggestions];
    updated[index] = value;
    setSuggestions(updated);
  };

  const addSuggestion = () => setSuggestions([...suggestions, ""]);
  const removeSuggestion = (index: number) =>
    setSuggestions(suggestions.filter((_, i) => i !== index));

  return (
    <Modal className="modal" open={open} onClose={onClose}>
      <div className="modal-dialog">
        <div className="modal-body">

          {/* Header */}
          <div className="modal_title">
            <h2>Add Step 8 - Question</h2>
            <button
              className="btn-close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>

          {/* Form */}
          <form className="form">
            <div className="gap_p">

              {/* Question Heading */}
              <div className="control_group">
                <label className="required" htmlFor="question-heading">Question Heading</label>
                <TextField
                  placeholder="Enter question heading"
                  fullWidth
                  hiddenLabel
                  inputProps={{ id: "question-heading" }}
                />
              </div>

              {/* Question Description */}
              <div className="control_group">
                <label htmlFor="question-description">Question Description</label>
                <TextField
                  placeholder="Describe the question (optional)"
                  fullWidth
                  multiline
                  rows={2}
                  hiddenLabel
                  inputProps={{ id: "question-description" }}
                />
              </div>

              {/* Question Type */}
              <div className="control_group w_100">
                <label className="required" htmlFor="question-type">Question Type</label>
                <Select
                  fullWidth
                  value={questionType}
                  onChange={(e) => setQuestionType(e.target.value)}
                  displayEmpty
                  inputProps={{ id: "question-type" }}
                >
                  <MenuItem value="" disabled>
                    Select Question Type
                  </MenuItem>
                  <MenuItem value="checkbox">Checkbox</MenuItem>
                  <MenuItem value="radio">Radio</MenuItem>
                  <MenuItem value="budget">Budget</MenuItem>
                  <MenuItem value="textarea">Textarea</MenuItem>
                </Select>
              </div>

              {/* Checkbox / Radio Fields */}
              {(questionType === "checkbox" || questionType === "radio") && (
                <>
                  <div className="control_group w_100">
                    <label>Upload Icon</label>
                    {image ? (
                      <div className="upload_image" style={{ marginBottom: "0 !important" }}>
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia
                              component="img"
                              image={image}
                              alt="photo"
                            />
                          </figure>
                          <CloseIcon
                            onClick={() => {
                              setImage("");
                              setFileName("");
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <label
                        className="upload_image"
                        htmlFor="icon-button-file"
                        style={{ marginBottom: "0 !important" }}
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(
                            event: ChangeEvent<HTMLInputElement>
                          ) => {
                            const files = (
                              event.target as HTMLInputElement
                            ).files;
                            if (
                              files &&
                              files[0].type.includes("image")
                            ) {
                              setFileName(files[0].name);
                              setImage(URL.createObjectURL(files[0]));
                            } else {
                              setAlertType(0);
                              setShowAlert(true);
                              setAlertMessage(
                                "This field only accepts images."
                              );
                            }
                          }}
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="icon"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="icon"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </div>

                  <div className="control_group w_50">
                    <label className="required" htmlFor="field-title">Field Title</label>
                    <TextField placeholder="Option title" fullWidth hiddenLabel inputProps={{ id: "field-title" }} />
                  </div>

                  <div className="control_group w_50">
                    <label htmlFor="field-info">Field Info</label>
                    <TextField
                      placeholder="Additional info"
                      fullWidth
                      hiddenLabel
                      inputProps={{ id: "field-info" }}
                    />
                  </div>
                </>
              )}

              {/* Budget Fields */}
              {questionType === "budget" && (
                <>
                  <div className="control_group w_50">
                    <label className="required" htmlFor="min-amount">Minimum Amount</label>
                    <TextField
                      placeholder="Min value"
                      type="number"
                      fullWidth
                      hiddenLabel
                      inputProps={{ id: "min-amount" }}
                    />
                  </div>

                  <div className="control_group w_50">
                    <label className="required" htmlFor="max-amount">Maximum Amount</label>
                    <TextField
                      placeholder="Max value"
                      type="number"
                      fullWidth
                      hiddenLabel
                      inputProps={{ id: "max-amount" }}
                    />
                  </div>
                </>
              )}

              {/* Textarea Fields */}
              {questionType === "textarea" && (
                <>
                  <div className="control_group">
                    <label className="required" htmlFor="placeholder-text">Placeholder Text</label>
                    <TextField
                      placeholder="Enter placeholder text"
                      fullWidth
                      hiddenLabel
                      inputProps={{ id: "placeholder-text" }}
                    />
                  </div>

                  <div className="control_group">
                    <label>Add Suggestions</label>

                    {suggestions.map((item, index) => (
                      <div
                        key={index}
                        style={{ display: "flex", gap: 8, marginBottom: 8 }}
                      >
                        <TextField
                          placeholder={`Suggestion ${index + 1}`}
                          fullWidth
                          value={item}
                          onChange={(e) =>
                            handleSuggestionChange(index, e.target.value)
                          }
                          hiddenLabel
                        />
                        <IconButton sx={{ minWidth: 45 }} onClick={() => removeSuggestion(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}

                    <Button
                      className="btn btn_grey sm"
                      onClick={addSuggestion}
                    >
                      <AddIcon /> Add Suggestion
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* <div className="control_group w_100" style={{ marginTop: 20 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', mb: 1 }}>
                  Service Accessibility
                </FormLabel>
                <RadioGroup row defaultValue="free">
                  <FormControlLabel value="free" control={<Radio size="small" />} label={<Typography variant="body2">Free Service</Typography>} />
                  <FormControlLabel value="paid" control={<Radio size="small" />} label={<Typography variant="body2">Paid Service (Premium)</Typography>} />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  Determine if this journey step is available for all users or restricted to premium plans.
                </Typography>
              </FormControl>
            </div> */}

            {/* Footer */}
            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>

              <Button
                className="btn btn_primary"
                onClick={() => setOpen(false)}
              >
                Save Question
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
